/**
 * Connection API Service
 *
 * Azure Management API calls for connection CRUD operations.
 * Based on LogicAppsUX patterns from designer-client-services.
 */

import type {
  ConnectionReference,
  ConnectionStatus,
  AvailableConnection,
} from '@/types/connectionReference';

import { getUniqueName } from './manifestConnectionService';
import { apiVersions, getLogicEndpoint } from './endpoints';
import { extractConnectorName, extractServiceProviderName, isManagedApiConnector, isServiceProviderConnector, toShortManagedApiId } from '@/utils/connections';
import { parseJsonBody } from './apiError';


// =============================================================================
// TYPES
// =============================================================================

/**
 * Azure Resource location for connection operations
 */
export interface AzureResourceContext {
  subscriptionId: string;
  resourceGroup: string;
  location: string;
  accessToken: string;
}

export interface AppResourceContext {
  accessToken: string;
  projectName: string;
  appId: string;
  location: string;
}

/**
 * Parameters for creating a new connection
 */
export interface CreateConnectionParams {
  /** Display name for the connection */
  connectionName: string;
  /** Connector/API resource ID */
  connectorId: string;
  /** Parameter values for the connection */
  parameterValues: Record<string, unknown>;
  /** Non-secret connection parameters persisted on the connection resource */
  connectionParameters?: Record<string, unknown>;
  /** Connection parameter set name (for multi-auth connectors) */
  parameterSetName?: string;
  /** Whether this is a first-party OAuth connection */
  isFirstPartyConnection?: boolean;
  /** Sync connection after successful connection creation
   * Default is true, should be set to false for OAuth connection since consent flow needs to be completed.
   */
  syncAfterCreation?: boolean; 
}

/**
 * Parameters for updating an existing connection
 */
export interface UpdateConnectionParams {
  /** Connection resource ID */
  connectionId: string;
  /** Updated parameter values (non-secret only) */
  parameterValues?: Record<string, unknown>;
  /** Updated display name */
  displayName?: string;
  /** Parameter set name (auth type) - when changing authentication method */
  parameterSetName?: string;
}

/**
 * Result of a connection test operation
 */
export interface TestConnectionResult {
  /** Whether the test was successful */
  success: boolean;
  /** Connection status after test */
  status: ConnectionStatus;
  /** Human-readable message */
  message?: string;
  /** Detailed error information */
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/**
 * Connection resource from Azure Management API
 */
export interface ConnectionResource {
  id: string;
  name: string;
  type: string;
  location: string;
  properties: {
    api?: {
      id: string;
      name: string;
      displayName?: string;
      iconUri?: string;
      brandColor?: string;
    };
    displayName?: string;
    statuses?: Array<{
      status: string;
      error?: {
        code: string;
        message: string;
      };
    }>;
    customParameterValues?: Record<string, unknown>;
    parameterValueSet?: {
      name: string;
      values: Record<string, unknown>;
    };
    /** Single-auth MI marker (Logic Apps Standard sets this to `'Alternative'`; see `isConnectionSingleAuthManagedIdentity`). */
    parameterValueType?: string;
    connectionParameters?: Record<string, unknown>;
    connectionRuntimeUrl?: string;
    createdTime?: string;
    changedTime?: string;
    testLinks?: Array<{
      requestUri: string;
      method: string;
    }>;
    testRequests?: Array<{
      uri: string;
      method: string;
    }>;
  };
}

export interface AppConnectionResource {
  id: string;
  name: string;
  type: string;
  properties: {
    changedTime: string;
    connectionId: string;
    connectionRuntimeUrl: string;
    connectorId: string;
    createdBy: {
      objectId: string;
      tenantId: string;
    };
    createdTime: string;
    displayName: string;
    overallStatus: string;
    provisioningState: string;
    statuses?: Array<{
      status: string;
      error?: {
        code: string;
        message: string;
      };
    }>;
  }
}

/**
 * Azure API error response
 */
export interface AzureApiError {
  error: {
    code: string;
    message: string;
    details?: Array<{
      code: string;
      message: string;
    }>;
  };
}

// =============================================================================
// CONSTANTS
// =============================================================================

const OTTO_API_VERSION = apiVersions.otto;

/**
 * ARM API version for Microsoft.Web/connections — same as Logic Apps Standard.
 * Used for create, listConsentLinks, confirmConsentCode on managed connectors.
 */

const isAppUrl = (url: string): boolean => url.toLowerCase().startsWith('/automationprojects/');
const getAppBasedUrl = (ctx: Omit<AppResourceContext, 'location'>): string => {
  if (ctx.appId && isAppUrl(ctx.appId)) {
    return `${getLogicEndpoint()}${ctx.appId}/connections`;
  }

  return `${ctx.appId}/connections`;
}

/**
 * Build base URL for project-scoped source control connections.
 * Uses `/automationProjects/{project}/sourceControlConnections` (no applications segment).
 */
const getSourceControlUrl = (projectName: string): string => {
  const encodedProjectName = encodeURIComponent(projectName);
  return `${getLogicEndpoint()}/automationProjects/${encodedProjectName}/sourceControlConnections`;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Build base URL for connection resources.
 * When useSourceControl is true, uses project-scoped sourceControlConnections routes.
 */
const buildConnectionsUrl = (ctx: Omit<AppResourceContext, 'location'>, connectionName?: string, useSourceControl?: boolean): string => {
  const base = useSourceControl
    ? getSourceControlUrl(ctx.projectName)
    : getAppBasedUrl(ctx);
  // Encode the connection name centrally so every connection URL is consistent and a name
  // with URL-special characters can't alter the request path.
  return connectionName ? `${base}/${encodeURIComponent(connectionName)}` : base;
};

const buildConnectorMetadataUrl = (projectName: string, connectorId: string): string => {
  const normalizedConnectorId = connectorId.startsWith('/') ? connectorId : `/${connectorId}`;
  const connectorPath = isAppUrl(normalizedConnectorId)
    ? normalizedConnectorId
    : `/automationProjects/${encodeURIComponent(projectName)}${normalizedConnectorId}`;

  return `${getLogicEndpoint()}${connectorPath}?api-version=${OTTO_API_VERSION}`;
};

/**
 * Make authenticated request to Azure Management API.
 */
async function appFetch<T>(
  url: string,
  accessToken: string,
  options: RequestInit & { skipAuthorization?: boolean } = {}
): Promise<T> {
  const { skipAuthorization = false, ...requestOptions } = options;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(requestOptions.headers as Record<string, string>),
  };

  if (!skipAuthorization) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    ...requestOptions,
    headers,
  });

  if (!response.ok) {
    let errorData: AzureApiError | null = null;
    const text = await response.text();
    const parsed = parseJsonBody(text);
    if (parsed && typeof parsed === 'object') {
      errorData = parsed as AzureApiError;
    }

    const errorMessage = errorData?.error?.message ?? `Request failed: ${response.status}`;
    const error = new Error(errorMessage) as Error & { code?: string | undefined; status?: number };
    error.code = errorData?.error?.code ?? undefined;
    error.status = response.status;
    throw error;
  }

  // Handle empty responses (204 No Content, or empty body)
  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}

/**
 * Extract the short connection name from a potentially pathed resource name.
 * The Otto data-plane API returns names like "project/app/connection-name";
 * this strips the path prefix and returns only the last segment.
 */
export function extractConnectionName(name: string): string {
  if (!name) return name;
  const parts = name.split('/');
  return parts[parts.length - 1] ?? name;
}

/**
 * Derive connection status from a ConnectionResource.
 * Prefers overallStatus (authoritative for Otto data-plane responses)
 * over the properties.statuses array.
 */
function deriveConnectionStatus(connection: ConnectionResource): ConnectionStatus {
  const overallStatus = (connection as unknown as AppConnectionResource).properties?.overallStatus;
  if (overallStatus) {
    const normalized = overallStatus.toLowerCase();
    if (normalized === 'connected' || normalized === 'ready') return 'connected';
    if (normalized === 'unauthenticated' || normalized === 'notconnected') return 'unauthenticated';
    return 'error';
  }
  return mapConnectionStatus(connection.properties.statuses);
}

/**
 * Map Azure connection status to our status type
 */
function mapConnectionStatus(
  statuses?: Array<{ status: string; error?: { code: string; message: string } }>
): ConnectionStatus {
  if (!statuses || statuses.length === 0) {
    return 'connected';
  }

  const primaryStatus = statuses[0];
  if (!primaryStatus) {
    return 'connected';
  }

  const status = primaryStatus.status.toLowerCase();

  if (status === 'connected' || status === 'ready') {
    return 'connected';
  }
  if (status === 'unauthenticated' || status === 'error' && primaryStatus.error?.code === 'Unauthenticated') {
    return 'unauthenticated';
  }
  return 'error';
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Normalize a connector ID to the canonical data-plane connector format.
 * Handles:
 *   - Service provider: `/automationProjects/.../serviceProviders/github/operations/foo` → `/serviceProviders/github`
 *   - Already canonical: `/managedApis/office365` → `/managedApis/office365`
 *   - Data-plane path: `/automationProjects/.../managedApis/office365` → `/managedApis/office365`
 *   - ARM path: `/subscriptions/.../managedApis/office365` → `/managedApis/office365`
 */
function toConnectionConnectorId(connectorId: string): string {
  if (isServiceProviderConnector(connectorId)) {
    return `/serviceProviders/${extractServiceProviderName(connectorId)}`;
  }

  return toShortManagedApiId(connectorId);
}

/**
 * Create a new connection resource in Automation app.
 * When useSourceControl is true, uses project-scoped sourceControlConnections routes.
 */
export async function createConnection(
  ctx: AppResourceContext,
  params: CreateConnectionParams,
  useSourceControl?: boolean,
): Promise<ConnectionResource> {
  const url = `${buildConnectionsUrl(ctx, params.connectionName, useSourceControl)}?api-version=${OTTO_API_VERSION}`;

  // For multi-auth connectors (e.g., Service Bus with ManagedIdentity / ConnectionString
  // parameter sets) the Azure managed-API schema requires `parameterValueSet: { name, values }`
  // instead of separate `parameterValues` + `parameterSetName`. Mirrors LogicAppsUX
  // `_getRequestForCreateConnection`. See connection.ts in libs/logic-apps-shared/.../base.
  const properties: Record<string, unknown> = {
    displayName: params.connectionName,
    connectorId: toConnectionConnectorId(params.connectorId),
  };

  if (params.parameterSetName) {
    properties.parameterValueSet = {
      name: params.parameterSetName,
      values: Object.fromEntries(
        Object.entries(params.parameterValues ?? {})
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, { value }])
      ),
    };
  } else {
    properties.parameterValues = params.parameterValues;
  }

  const connection = await appFetch<ConnectionResource>(url, ctx.accessToken, {
    method: 'PUT',
    body: JSON.stringify({ properties }),
  });

  if (params.syncAfterCreation === false || useSourceControl) {
    // Skip sync when explicitly disabled or when using project-scoped
    // sourceControlConnections routes (no /sync endpoint exists).
    return connection;
  }

  try {
    await syncConnection(ctx, params.connectionName);
    return connection;
  } catch (error) {  // If sync fails, attempt to delete the created connection to avoid leaving broken connections
    console.warn('Connection created but failed to sync. Attempting to delete the connection to avoid leaving it in a broken state.', error);
    try {
      await deleteConnection(ctx, params.connectionName, useSourceControl);
    } catch (deleteError) {
      console.warn('Failed to delete the connection after sync failure.', deleteError);
    }
    
    throw error;
  }
}

export async function syncConnection(
  ctx: Omit<AppResourceContext, 'location'>,
  connectionName: string,
): Promise<void> {
  const url = `${buildConnectionsUrl(ctx, connectionName)}/sync?api-version=${OTTO_API_VERSION}`;
  await appFetch<void>(url, ctx.accessToken, {
    method: 'POST',
  });
}

/**
 * Delete a connection resource
 */
export async function deleteConnection(
  ctx: AppResourceContext,
  connectionName: string,
  useSourceControl?: boolean,
): Promise<void> {
  const url = `${buildConnectionsUrl(ctx, connectionName, useSourceControl)}?api-version=${OTTO_API_VERSION}`;

  await appFetch<void>(url, ctx.accessToken, {
    method: 'DELETE',
  });
}

/**
 * Check if a connection name is unique (doesn't exist) in Azure API Hub.
 * Returns true if unique (404 = not found), false if exists (200 or error).
 * 
 * This follows the pattern from LogicAppsUX:
 * - 404 = unique (name is available) ✅
 * - 200 = exists (name is taken) ❌  
 * - Other errors = assume not unique (safe default) ❌
 */
export async function isConnectionNameUnique(
  ctx: AppResourceContext,
  connectionName: string,
  useSourceControl?: boolean,
): Promise<boolean> {
  const url = `${buildConnectionsUrl(ctx, connectionName, useSourceControl)}?api-version=${OTTO_API_VERSION}`;
  try {
    await appFetch<ConnectionResource>(url, ctx.accessToken);
    return false; // 200 = exists = NOT unique
  } catch (error) {
    // Check for 404 status - this means the connection doesn't exist = IS unique
    const err = error as Error & { status?: number };
    if (err.status === 404) {
      return true; // 404 = not found = IS unique
    }
    // For other errors, assume not unique (safe default)
    return false;
  }
}

/**
 * Get a connection resource by name
 */
export async function getConnection(
  ctx: AppResourceContext,
  connectionName: string,
  useSourceControl?: boolean,
): Promise<ConnectionResource> {
  const url = `${buildConnectionsUrl(ctx, connectionName, useSourceControl)}?api-version=${OTTO_API_VERSION}`;
  return appFetch<ConnectionResource>(url, ctx.accessToken);
}

/**
 * Shape of a paginated connection-list response. The data-plane emits
 * `@odata.nextLink`; ARM-style responses emit `nextLink`. We accept either.
 */
interface ConnectionListResponse {
  value?: ConnectionResource[];
  nextLink?: string;
  '@odata.nextLink'?: string;
}

/**
 * Result of a full connection-list walk across every `nextLink` page.
 *
 * `truncated` is `true` when the last fetched page still returned a live
 * `@odata.nextLink` after `MAX_PAGES` iterations — i.e. the server has more
 * pages than we followed. Callers MUST surface this so the UI never shows a
 * silently-partial list (matching the reviewer's "fail loudly" contract on
 * PR #2522). `fetchedPages` is the number of HTTP calls the walk actually
 * made (before de-duplication).
 */
export interface ListConnectionsResult {
  items: ConnectionResource[];
  truncated: boolean;
  fetchedPages: number;
}

/**
 * Hard cap on `nextLink` pages we will follow. Exported so tests and UI
 * copy can name a specific "we fetched at most N pages" figure.
 */
export const CONNECTIONS_MAX_PAGES = 50;

/**
 * Verify a server-provided `nextLink` stays on the same origin as the
 * initial request. `appFetch` unconditionally attaches the caller's bearer
 * token to every URL it fetches — so a compromised or hostile response
 * body containing a cross-origin `nextLink` (e.g. `https://attacker.example/…`)
 * could exfiltrate that token. This is the security guard the reviewer
 * flagged on PR #2522: reject any `nextLink` that doesn't resolve to the
 * same origin as the initial connections URL (including malformed ones).
 * Relative URLs are resolved against the initial URL before checking, so a
 * spec-compliant same-origin relative `nextLink` still walks correctly.
 */
function resolveSafeNextLink(
  nextLink: string,
  baseUrl: string,
  expectedOrigin: string | null,
): string | null {
  // No trusted origin to compare against → refuse to follow. This is only
  // hit in pathological configurations (unconfigured logic endpoint in a
  // test env); the fallback is safe rather than clever.
  if (!expectedOrigin) return null;
  try {
    const resolved = new URL(nextLink, baseUrl);
    if (resolved.origin !== expectedOrigin) return null;
    return resolved.toString();
  } catch {
    return null;
  }
}

/**
 * Escape a value for embedding inside a single-quoted OData string literal
 * (`$filter=foo eq 'value'`). The OData spec says a literal single quote is
 * encoded by doubling it (`''`). Without this, a `'` in the value breaks the
 * expression at best and, at worst, opens a filter-injection vector against
 * the ARM/connections endpoint. This is the guard the review flagged on
 * PR #2522.
 */
function encodeODataString(value: string): string {
  return value.replace(/'/g, "''");
}

/**
 * List all connections in an application, optionally filtering by connector ID.
 * When useSourceControl is true, lists project-scoped source control connections.
 *
 * Follows server-driven pagination so connections beyond the first page are
 * not silently dropped (which would also make them read as "not found" during
 * connection-status validation). The loop is bounded four ways:
 *   1. A visited-URL set breaks on a self-referential/cyclic `nextLink`.
 *   2. A hard page cap (`CONNECTIONS_MAX_PAGES`) guards against an unbounded
 *      service. When we stop at the cap with a live `nextLink` still present,
 *      the result is flagged `truncated: true` — the UI surfaces an explicit
 *      warning instead of silently rendering a partial list.
 *   3. A same-origin check on every `nextLink` guards against bearer-token
 *      exfiltration via a hostile `@odata.nextLink` pointing at a different
 *      origin. A cross-origin (or malformed) `nextLink` breaks the walk and
 *      flags the result as truncated so the UI is honest about what it shows.
 *   4. Results are de-duplicated by `id` so overlapping skiptoken windows
 *      never surface duplicate connections downstream.
 */
export async function listConnectionsWithMeta(
  ctx: AppResourceContext,
  connectorId?: string,
  useSourceControl?: boolean,
): Promise<ListConnectionsResult> {
  let baseUrl = `${buildConnectionsUrl(ctx, undefined, useSourceControl)}?api-version=${OTTO_API_VERSION}`;

  // Filter by connector if specified
  if (connectorId) {
    const connectorName = extractConnectorName(connectorId);
    // OData single-quote escape (spec: doubled) — connectorId is a caller
    // string that could plausibly contain a `'` (managed API ids come from
    // ARM but connector-name suffixes are user-controlled downstream). Without
    // escaping, an embedded `'` breaks the filter expression and, worst case,
    // enables filter-injection against the ARM endpoint.
    const encodedId = encodeODataString(connectorId);
    const encodedName = encodeODataString(connectorName);
    baseUrl += `&$filter=properties/api/id eq '${encodedId}' or properties/api/name eq '${encodedName}'`;
  }

  // `initialUrl` is captured as a definite string BEFORE the mutable `url`
  // walker is declared — so `resolveSafeNextLink(_, initialUrl, _)` (which
  // needs a definite base) and the initial-origin derivation both operate on
  // a value TypeScript can't widen to `undefined`. If we instead read from
  // the mutable `url` after the loop starts reassigning it, a future
  // refactor could silently drop the narrowing.
  const initialUrl: string = baseUrl;

  // Origin the initial URL lives on — every follow-up `nextLink` MUST match
  // this. We build the initial URL ourselves so its origin is trusted, and
  // we anchor every subsequent server-provided URL against it. If the initial
  // URL is somehow malformed (e.g. an unconfigured endpoint in a test env),
  // fall back to a null origin — the safe-nextLink resolver rejects
  // everything, so pagination is effectively disabled instead of following an
  // untrusted URL with the bearer token attached.
  let initialOrigin: string | null;
  try {
    initialOrigin = new URL(initialUrl).origin;
  } catch {
    initialOrigin = null;
  }

  let url: string | undefined = initialUrl;

  const seenUrls = new Set<string>();
  const byId = new Map<string, ConnectionResource>();
  let page = 0;
  // `walkIncomplete` records that we cut the walk short before exhausting the
  // pages — either a page returned a cross-origin/malformed `nextLink`, or a
  // repeated URL revealed a cyclic pagination chain. The caller treats this
  // the same as a cap hit (list is incomplete → surface truncation) so the
  // UI can warn instead of silently rendering a partial list.
  let walkIncomplete = false;
  while (url && page < CONNECTIONS_MAX_PAGES) {
    if (seenUrls.has(url)) {
      // Cyclic `nextLink` (server returned a page URL we've already fetched).
      // Break out of the walk AND flag truncation — leaving `walkIncomplete`
      // false here would let the caller believe the list is complete when it
      // isn't.
      walkIncomplete = true;
      break;
    }
    seenUrls.add(url);
    page++;

    // `appFetch` returns `undefined` for a 204/empty page; guard every access.
    const response = (await appFetch<ConnectionListResponse>(url, ctx.accessToken)) as
      | ConnectionListResponse
      | undefined;
    for (const connection of response?.value ?? []) {
      byId.set(connection.id, connection);
    }
    const rawNext = response?.['@odata.nextLink'] ?? response?.nextLink;
    if (!rawNext) {
      url = undefined;
      break;
    }
    const safeNext = resolveSafeNextLink(rawNext, initialUrl, initialOrigin);
    if (safeNext === null) {
      // Cross-origin or malformed — do NOT follow (would leak the bearer
      // token to a different origin). Stop the walk and flag truncation.
      walkIncomplete = true;
      url = undefined;
      break;
    }
    url = safeNext;
  }

  // A live `nextLink` still present at the cap OR an early-exit (cyclic
  // nextLink / hostile-origin nextLink rejection) both mean the service has
  // more pages than we surfaced. Either way the caller renders an explicit UI
  // warning so a genuinely large tenant (or a hostile/broken response) is
  // *observable* rather than silently rendered as a partial list.
  const truncated = (!!url && page >= CONNECTIONS_MAX_PAGES) || walkIncomplete;

  return {
    items: Array.from(byId.values()),
    truncated,
    fetchedPages: page,
  };
}

/**
 * Backward-compatible wrapper returning just the connection array. Existing
 * callers (`useConnections`, connection-status validation, etc.) don't need
 * the pagination metadata; only the picker header does — it uses the walk
 * result via `listConnectionsWithMeta` directly.
 */
export async function listConnections(
  ctx: AppResourceContext,
  connectorId?: string,
  useSourceControl?: boolean,
): Promise<ConnectionResource[]> {
  const { items } = await listConnectionsWithMeta(ctx, connectorId, useSourceControl);
  return items;
}

/**
 * Test a connection's validity
 */
export async function testConnection(
  ctx: AppResourceContext,
  connectionName: string,
  useSourceControl?: boolean,
): Promise<TestConnectionResult> {
  try {
    // First, get the connection to check its status
    const connection = await getConnection(ctx, connectionName, useSourceControl);

    // Check if connection has test links
    const testLink = connection.properties.testLinks?.[0] ?? connection.properties.testRequests?.[0];

    if (testLink) {
      // Execute the test request
      const testLinkWithUri = testLink as { requestUri?: string; uri?: string; method: string };
      const testUrl = testLinkWithUri.requestUri ?? testLinkWithUri.uri;
      const testMethod = testLink.method ?? 'GET';

      if (testUrl) {
        try {
          await appFetch<unknown>(testUrl, ctx.accessToken, {
            method: testMethod,
          });

          return {
            success: true,
            status: 'connected',
            message: 'Connection test successful',
          };
        } catch (testError) {
          const error = testError as Error & { code?: string };
          return {
            success: false,
            status: error.code === 'Unauthenticated' ? 'unauthenticated' : 'error',
            message: 'Connection test failed',
            error: {
              code: error.code ?? 'TestFailed',
              message: error.message,
            },
          };
        }
      }
    }

    // Fall back to status check (Otto automation connections don't have testLinks).
    const status = deriveConnectionStatus(connection);
    return {
      success: status === 'connected',
      status,
      message: status === 'connected' ? 'Connection is valid' : 'Connection has issues',
    };
  } catch (error) {
    const err = error as Error & { code?: string };
    return {
      success: false,
      status: 'error',
      message: 'Failed to test connection',
      error: {
        code: err.code ?? 'Unknown',
        message: err.message,
      },
    };
  }
}

/**
 * Get connection status
 */
export async function getConnectionStatus(
  ctx: AppResourceContext,
  connectionName: string,
  useSourceControl?: boolean,
): Promise<ConnectionStatus> {
  try {
    const connection = await getConnection(ctx, connectionName, useSourceControl);
    return deriveConnectionStatus(connection);
  } catch {
    return 'error';
  }
}

/**
 * Update connection parameters (non-secret values only)
 */
export async function updateConnection(
  ctx: AppResourceContext,
  connectionName: string,
  params: UpdateConnectionParams
): Promise<ConnectionResource> {
  // Get existing connection first
  const existing = await getConnection(ctx, connectionName);

  const url = `${buildConnectionsUrl(ctx, connectionName)}?api-version=${OTTO_API_VERSION}`;

  const body = {
    properties: {
      ...existing.properties,
      ...(params.displayName && { displayName: params.displayName }),
      // When changing auth type (parameterSetName), use parameterValueSet structure.
      // Each value must be wrapped in `{ value }` (Azure managed-API schema; matches
      // createConnection and LogicAppsUX `_getRequestForCreateConnection`). Sending
      // raw scalars here causes the Otto control plane to return 500.
      // Also explicitly drop any inherited `parameterValues` so we don't send both
      // schemas in the same PUT — JSON.stringify omits `undefined`-valued keys.
      ...(params.parameterSetName && {
        parameterValueSet: {
          name: params.parameterSetName,
          values: Object.fromEntries(
            Object.entries(params.parameterValues ?? {})
              .filter(([, value]) => value !== undefined)
              .map(([key, value]) => [key, { value }])
          ),
        },
        parameterValues: undefined,
      }),
      // When only updating values (no auth type change), merge with existing parameterValues
      ...(!params.parameterSetName && params.parameterValues && {
        parameterValues: params.parameterValues,
      }),
    },
  };

  return appFetch<ConnectionResource>(url, ctx.accessToken, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

type ConsentLinkResponse = {
  value: Array<{
    link: string;
    firstPartyLoginUri?: string;
    displayName?: string;
    status?: string;
  }>;
};

type ListConsentLinksOptions = {
  connectionName: string;
  redirectUrl: string;
  objectId: string;
  tenantId: string;
};

export const listConsentLinks = async (
  ctx: AppResourceContext,
  opts: ListConsentLinksOptions,
  useSourceControl?: boolean,
): Promise<{ consentLink: string; firstPartyLoginUri?: string }> => {
  const url = `${buildConnectionsUrl(ctx, opts.connectionName, useSourceControl)}/listConsentLinks?api-version=${OTTO_API_VERSION}`;

  const requestBody = {
    parameters: [
      {
        parameterName: 'token',
        redirectUrl: opts.redirectUrl,
        objectId: opts.objectId,
        tenantId: opts.tenantId,
      },
    ],
  };

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${ctx.accessToken}`,
  };

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get consent links: ${response.status} ${errorText}`);
  }

  const data: ConsentLinkResponse = await response.json();
  const consentInfo = data.value[0];

  if (!consentInfo?.link) {
    throw new Error('No consent link returned');
  }

  const result: { consentLink: string; firstPartyLoginUri?: string } = {
    consentLink: consentInfo.link,
  };

  if (consentInfo.firstPartyLoginUri) {
    result.firstPartyLoginUri = consentInfo.firstPartyLoginUri;
  }

  return result;
};

type ConfirmConsentCodeOptions = {
  connectionName: string;
  code: string;
  objectId: string;
  tenantId: string;
};

export const confirmConsentCode = async (ctx: AppResourceContext, opts: ConfirmConsentCodeOptions, useSourceControl?: boolean): Promise<void> => {
  const url = `${buildConnectionsUrl(ctx, opts.connectionName, useSourceControl)}/confirmConsentCode?api-version=${OTTO_API_VERSION}`;

  const confirmHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${ctx.accessToken}`,
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: confirmHeaders,
    body: JSON.stringify({
      code: opts.code,
      objectId: opts.objectId,
      tenantId: opts.tenantId,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to confirm consent: ${response.status} ${errorText}`);
  }
};

// =============================================================================
// LIST MODELS (GHCP)
// =============================================================================

/**
 * Response shape from the runtime's listModels endpoint.
 * Mirrors the GitHub Copilot /models API response.
 */
export interface GhcpModel {
  id: string;
  name: string;
  version: string;
  model_picker_enabled?: boolean;
}

export interface ListModelsResponse {
  data: GhcpModel[];
}

/**
 * List models available via a GitHub Copilot agent connection.
 *
 * Calls the data-plane front door at
 * `GET .../automationProjects/{project}/applications/{app}/connections/{connectionName}/models`.
 * The data plane authenticates the portal token and proxies to the runtime
 * management route, which forwards through Token Broker to obtain the
 * connection token from CRP before calling the GitHub Copilot models endpoint.
 * (The ACA app FQDN cannot be called directly — it does not accept the
 * portal's data-plane token.)
 */
export async function listConnectionModels(
  ctx: Omit<AppResourceContext, 'location'>,
  connectionName: string,
): Promise<GhcpModel[]> {
  const url = `${buildConnectionsUrl(ctx, connectionName)}/models?api-version=${OTTO_API_VERSION}`;
  try {
    const response = await appFetch<ListModelsResponse>(url, ctx.accessToken);
    // Drop models GitHub marks as not picker-selectable (e.g. embeddings, gated/deprecated
    // entries); they'd fail if chosen. Keep entries where the flag is absent for compatibility.
    return (response.data ?? []).filter((model) => model.model_picker_enabled !== false);
  } catch (error) {
    // The runtime /models sub-route isn't deployed everywhere yet. When it's
    // absent the call 404s — degrade to an empty list so the freeform model
    // combobox stays usable (the user can type the model name manually)
    // instead of surfacing a hard error. Other failures still propagate.
    const err = error as Error & { status?: number };
    if (err.status === 404) {
      return [];
    }
    throw error;
  }
}

/**
 * Convert ConnectionResource to AvailableConnection for UI
 */
export function toAvailableConnection(resource: ConnectionResource): AvailableConnection {
  const resourceName = extractConnectionName(resource.name);
  const result: AvailableConnection = {
    id: resource.id,
    name: resourceName,
    displayName: resource.properties.displayName ?? resourceName,
    connectorId: (resource as unknown as AppConnectionResource).properties.connectorId ?? resource.properties.api?.id,
    status: deriveConnectionStatus(resource),
    properties: {},
  };

  if (resource.properties.createdTime) {
    result.createdTime = resource.properties.createdTime;
  }
  if (resource.properties.changedTime) {
    result.lastModifiedTime = resource.properties.changedTime;
  }
  if (result.properties) {
    if (resource.properties.customParameterValues) {
      result.properties.customParameterValues = resource.properties.customParameterValues;
    }
    if (resource.properties.parameterValueSet) {
      result.properties.parameterValueSet = resource.properties.parameterValueSet;
    }
  }

  return result;
}

/**
 * Convert ConnectionResource to ConnectionReference for workflow
 */
export function toConnectionReference(resource: ConnectionResource): ConnectionReference {
  // Data-plane responses use connectorId instead of api.id — check both
  const rawApiId = resource.properties.api?.id
    ?? (resource as unknown as AppConnectionResource).properties.connectorId
    ?? '';
  // Normalize managed API IDs to canonical /managedApis/{name} format
  const apiId = isManagedApiConnector(rawApiId) ? toShortManagedApiId(rawApiId) : rawApiId;

  const result: ConnectionReference = {
    api: { id: apiId },
    connection: { id: resource.id },
    connectionName: extractConnectionName(resource.name),
  };

  if (resource.properties.connectionRuntimeUrl) {
    result.connectionRuntimeUrl = resource.properties.connectionRuntimeUrl;
  }
  if (resource.properties.connectionParameters) {
    result.connectionProperties = resource.properties.connectionParameters;
  }

  return result;
}

/**
 * Fetch connection runtime URL from get connections api.
 */
export async function fetchConnectionRuntimeUrl(
  ctx: AppResourceContext,
  connectionId: string
): Promise<string | undefined> {
  const connection = await getConnection(ctx, connectionId.split('/').slice(-1)[0] ?? '');
  return connection.properties.connectionRuntimeUrl;
}

/**
 * Backfill missing connectionRuntimeUrl values in a connections config.
 *
 * Iterates over managed API connections and fetches the runtime URL via
 * listConnectionKeys for any entry that has an empty or missing value.
 * Mutates the config in-place and returns the keys that were updated.
 */

// =============================================================================
// CONNECTOR METADATA
// =============================================================================

/**
 * Get connector metadata (including connection parameters)
 */
export interface ConnectorMetadata {
  id: string;
  name: string;
  displayName: string;
  iconUri?: string;
  brandColor?: string;
  connectionParameters?: Record<string, unknown>;
  connectionParameterSets?: Array<{
    name: string;
    uiDefinition: {
      displayName: string;
      description?: string;
    };
    parameters: Record<string, unknown>;
  }>;
  /** Alternative connection parameters (e.g., managed identity auth for legacy OAuth connectors) */
  connectionAlternativeParameters?: Record<string, unknown>;
}

export async function getConnectorMetadata(
  context: AppResourceContext,
  connectorId: string
): Promise<ConnectorMetadata> {
  const url = buildConnectorMetadataUrl(context.projectName, connectorId);

  const response = await appFetch<{
    id: string;
    name: string;
    properties: {
      generalInformation?: {
        displayName: string;
        iconUrl?: string;
        description?: string;
      };
      metadata?: {
        brandColor?: string;
      };
      connectionParameters?: Record<string, unknown>;
      connectionParameterSets?: {
        values: Array<{
          name: string;
          uiDefinition: {
            displayName: string;
            description?: string;
          };
          parameters: Record<string, unknown>;
        }>;
      };
      connectionAlternativeParameters?: Record<string, unknown>;
    };
  }>(url, context.accessToken);

  const result: ConnectorMetadata = {
    id: response.id,
    name: response.name,
    displayName: response.properties.generalInformation?.displayName ?? response.name,
  };

  if (response.properties.generalInformation?.iconUrl) {
    result.iconUri = response.properties.generalInformation.iconUrl;
  }
  if (response.properties.metadata?.brandColor) {
    result.brandColor = response.properties.metadata.brandColor;
  }
  if (response.properties.connectionParameters) {
    result.connectionParameters = response.properties.connectionParameters;
  }
  if (response.properties.connectionParameterSets?.values) {
    result.connectionParameterSets = response.properties.connectionParameterSets.values;
  }
  if (response.properties.connectionAlternativeParameters) {
    result.connectionAlternativeParameters = response.properties.connectionAlternativeParameters;
  }

  return result;
}

// =============================================================================
// UNIQUE CONNECTION NAMING
// =============================================================================

/**
 * Gets a unique connection name, checking both locally and against Azure API Hub.
 *
 * @param ctx - Azure resource context
 * @param connectorId - The connector's resource ID
 * @param connectionNames - Array of known connection names (local check)
 * @param connectorName - The connector's display name (used as prefix)
 * @returns Promise resolving to a unique connection name
 */
export async function getUniqueConnectionName(
  ctx: AppResourceContext,
  connectorId: string,
  connectionNames: string[],
  connectorName: string
): Promise<string> {
  // Clean the connector name for use as base
  const baseName = connectorName
    .replace(/[^a-zA-Z0-9-]/g, '')
    .substring(0, 20) || 'connection';

  // First, get a locally unique name
  const { name: connectionName, index } = getUniqueName(connectionNames, baseName);

  // For App resources, also verify against Azure API Hub
  if (isAppUrl(connectorId)) {
    return getUniqueConnectionNameInApp(ctx, baseName, connectionName, index, connectionNames);
  }

  return connectionName;
}

/**
 * Maximum number of attempts to find a unique connection name.
 * Prevents infinite recursion in case of API failures.
 */
const MAX_UNIQUE_NAME_ATTEMPTS = 100;

/**
 * Recursively checks API Hub until a truly unique name is found.
 * 
 * Uses the pattern from LogicAppsUX where:
 * - 404 = unique (connection doesn't exist, name is available)
 * - 200 = exists (connection exists, try next name)
 */
async function getUniqueConnectionNameInApp(
  ctx: AppResourceContext,
  baseName: string,
  connectionName: string,
  index: number,
  connectionNames: string[] = []
): Promise<string> {
  // Prevent infinite recursion - if we've tried too many times, use timestamp fallback
  if (index >= MAX_UNIQUE_NAME_ATTEMPTS) {
    const timestamp = Date.now();
    console.warn(`[connectionApi] Exceeded max attempts (${MAX_UNIQUE_NAME_ATTEMPTS}) for unique name, using timestamp fallback`);
    return `${baseName}-${timestamp}`;
  }

  // Skip API call if we already know this name exists locally
  const normalizedNames = connectionNames.map(n => n.toLowerCase());
  if (!normalizedNames.includes(connectionName.toLowerCase())) {
    // Check if the name is unique in Azure API Hub
    const isUnique = await isConnectionNameUnique(ctx, connectionName);
    if (isUnique) {
      // 404 = name is available, use it!
      return connectionName;
    }
  }

  // Name is taken (either locally or in Azure), try the next suffix
  const nextIndex = index + 1;
  const nextName = `${baseName}-${nextIndex}`;
  return getUniqueConnectionNameInApp(ctx, baseName, nextName, nextIndex, connectionNames);
}
